<template>
	<div class="fixed inset-0 z-30 flex items-center justify-center p-4 bg-black/40" @click="emit('close')">
		<div class="w-full max-w-sm p-4 bg-white rounded-lg" @click.stop>
			<form class="flex flex-col gap-4" @submit.prevent="checkVouchers">
				<h3 class="font-serif text-2xl">{{ t('redeem_your_vouchers_here') }}</h3>
				<p class="mb-4">{{ t('redeem_your_vouchers_and_gift_vouchers_during_payment_step') }}</p>
				<div v-for="(v, i) in vouchers" :key="`voucher_code_${i}`" class="relative">
					<div class="relative">
						<input
							id="textfield_681"
							v-model="vouchers[i]"
							type="text"
							placeholder=" "
							class="block w-full py-2.5 pl-4 pr-14 h-14 rounded-t bg-neutral-50 border-0 border-b-2 appearance-none focus:outline-none focus:ring-0 peer text-neutral-400 border-neutral-400 focus:border-primary-500"
						/>
						<label
							for="textfield_681"
							class="pointer-events-none absolute text-[#7e7e7e] duration-300 transform -translate-y-6 scale-75 left-4 top-3 origin-[0] peer-focus:text-primary peer-focus:dark:text-primary-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
						>
							{{ t('voucher_code') }}
							<span v-if="vouchersWithError.includes(`voucher_codes.${i}`)" class="text-red-500">*</span>
						</label>
						<button
							type="button"
							class="absolute inline-flex items-center justify-center w-10 h-10 text-neutral-500 rounded-full right-2 top-2 hover:bg-black/5"
							@click="removeVoucherCode(i)"
						>
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="h-6">
								<path
									fill="currentColor"
									fill-rule="nonzero"
									d="M19.284 18.216a.76.76 0 0 1 0 1.068.769.769 0 0 1-1.068 0L12 13.06l-6.216 6.225a.769.769 0 0 1-1.068 0 .76.76 0 0 1 0-1.068L10.94 12 4.716 5.784a.76.76 0 0 1 1.068-1.068L12 10.94l6.216-6.225a.76.76 0 0 1 1.068 1.068L13.06 12l6.225 6.216Z"
								/>
							</svg>
						</button>
					</div>
				</div>

				<div v-if="generalVoucherError" class="text-xs text-red-500">* {{ generalVoucherError }}</div>

				<button
					v-if="vouchers.length < 4"
					type="button"
					class="flex w-full gap-2 items-center my-2"
					@click="addVoucherCode"
				>
					<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24" class="pointer-events-none">
						<path
							fill="currentColor"
							fill-rule="nonzero"
							d="M21.3 9.797a1.51 1.51 0 0 0 1.2-1.472V6A1.5 1.5 0 0 0 21 4.5H3A1.5 1.5 0 0 0 1.5 6v2.325a1.51 1.51 0 0 0 1.2 1.472 2.25 2.25 0 0 1 0 4.406 1.51 1.51 0 0 0-1.2 1.472V18A1.5 1.5 0 0 0 3 19.5h18a1.5 1.5 0 0 0 1.5-1.5v-2.325a1.51 1.51 0 0 0-1.2-1.472 2.25 2.25 0 0 1 0-4.406ZM3 15.675a3.75 3.75 0 0 0 0-7.35V6h5.25v12H3v-2.325Zm18 0V18H9.75V6H21v2.325a3.75 3.75 0 0 0 0 7.35Z"
						/>
					</svg>
					{{ t('add_extra_voucher') }}
				</button>

				<div class="flex gap-4 max-md:flex-col">
					<button type="button" class="btn-secondary max-md:w-full max-md:order-last" @click="emit('close')">
						{{ t('cancel') }}
					</button>
					<button type="submit" class="btn-primary max-md:w-full md:flex-1 max-md:order-first">
						{{ t('redeem') }}
					</button>
				</div>
			</form>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { isAxiosError } from 'axios';
import { ref } from 'vue';
import bridge from '@thermenresorts/bridge';
import { useApi } from '../api';
import { useI18n } from 'vue-i18n';

export type Props = {
  workspaceId: number
}

const props = defineProps<Props>();

const emit = defineEmits([
  'close',
]);

const api = useApi();
const { t } = useI18n();

const vouchers = ref<string[]>(['']);
const generalVoucherError = ref<string>();
const vouchersWithError = ref<string[]>([]);

const addVoucherCode = () => {
  vouchers.value.push('');
  generalVoucherError.value = undefined;
  vouchersWithError.value = [];
};

const removeVoucherCode = (index: number) => {
  vouchers.value = [
    ...vouchers.value.slice(0, index),
    ...vouchers.value.slice(index + 1),
  ];

  if (vouchers.value.length === 0) {
    vouchers.value = [''];
  }

  generalVoucherError.value = undefined;
  vouchersWithError.value = [];
};

const checkVouchers = async () => {
  try {
    generalVoucherError.value = undefined;
    const response = await api.workspaces.checkVouchers(props.workspaceId, vouchers.value, null)
    bridge.sendMessage('pre-reservation-flow', 'reservation-flow.open', { url: response.data.reservation_flow_url });
    emit('close');
    vouchers.value = [''];
  } catch (error) {
    if (isAxiosError(error) && error.response?.data?.errors) {
      const errorKeys = Object.keys(error.response.data.errors);
      if (errorKeys.length > 0) {
        generalVoucherError.value = error.response.data.errors[errorKeys[0]][0];
        vouchersWithError.value = errorKeys;
      }
    } else {
      generalVoucherError.value = t('unable_to_redeem_voucher');
      vouchersWithError.value = ['voucher_codes.0', 'voucher_codes.1', 'voucher_codes.2', 'voucher_codes.3'];
      console.log(error);
    }
  }
};
</script>
